import {
  OnChangeFn,
  RowSelectionState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useEffect, useMemo } from "react";
import { Alert, Form } from "react-bootstrap";
import type { Household } from "../../../../api/src/households-base/households-base.service";
import Loading from "../../Loading";
import IndeterminateCheckbox from "../../components/IndeterminateCheckbox";
import { Table, useTable } from "../../components/Table/Table";
import { useQueryHouseholds } from "../household/lib";
import { householdGroupMembershipConflicts } from "./householdGroupMembershipConflicts";
import { useQueryHouseholdGroups } from "./lib";

// `useQueryHouseholds()` reads without balances, so each row is a `Household`.
const columnHelper = createColumnHelper<Household>();

const columns = [
  columnHelper.display({
    id: "select",
    cell: ({ row }) => (
      <div className="px-1">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  }),
  columnHelper.accessor((row) => row.name, {
    id: "name",
    header: () => "Name",
  }),
];

const quoted = (names: string[]) => names.map((name) => `"${name}"`).join(", ");

/**
 * One sentence for a household left out of the table: the household and each
 * group it is a member of on a date from `effectiveDate` on.
 */
const leftOutSentence = (
  householdName: string,
  groupNames: string[],
  effectiveDate: string,
) =>
  `Household "${householdName}" is a member of household ${groupNames.length === 1 ? "group" : "groups"} ${quoted(groupNames)} on a date from ${effectiveDate} on.`;

/**
 * A table of the firm's active households to choose the households of a
 * household group from. A household that is a member of another group on any
 * date from `effectiveDate` on is left out of the table
 * (s-household-groups-group-level-019f18f8), and a note under the table names
 * it and that group, with the closing sentence of the api's M1 refusal
 * (`overlappingMembershipsMessage`,
 * `api/src/household-groups/household-group-refusals.ts`).
 */
const HouseholdGroupHouseholdPicker = ({
  effectiveDate,
  rowSelection,
  setRowSelection,
  multiple,
}: {
  /** `YYYY-MM-DD`: the first day of the new membership. */
  effectiveDate: string;
  /** Keyed by household id. */
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  multiple: boolean;
}) => {
  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds();
  const {
    isPending: isPendingGroups,
    isError: isErrorGroups,
    data: groups,
  } = useQueryHouseholdGroups();

  const conflicts = useMemo(
    () => householdGroupMembershipConflicts(groups ?? [], effectiveDate),
    [groups, effectiveDate],
  );

  // A household selected before the date changed may now be a member of
  // another group on a date of the new membership. Its row leaves the table:
  // drop it from the selection too, so it is not sent.
  useEffect(() => {
    setRowSelection((selection) => {
      const kept = Object.fromEntries(
        Object.entries(selection).filter(
          ([householdId]) => !conflicts.has(Number(householdId)),
        ),
      );
      return Object.keys(kept).length === Object.keys(selection).length
        ? selection
        : kept;
    });
  }, [conflicts, setRowSelection]);

  const rows = useMemo(
    () =>
      (dataHouseholds?.data ?? []).filter(
        (household) => !conflicts.has(household.id),
      ),
    [dataHouseholds?.data, conflicts],
  );

  // The firm's active households the table leaves out, by name.
  const leftOut = useMemo(
    () =>
      (dataHouseholds?.data ?? [])
        .flatMap((household) => {
          const groupNames = conflicts.get(household.id);
          return groupNames === undefined
            ? []
            : [{ id: household.id, name: household.name, groupNames }];
        })
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)),
    [dataHouseholds?.data, conflicts],
  );

  const { table } = useTable({
    columns,
    data: rows,
    getRowId: (row) => row.id.toString(),
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableMultiRowSelection: multiple,
    initialState: { sorting: [{ id: "name", desc: false }] },
  });

  return isPendingHouseholds || isPendingGroups ? (
    <Loading />
  ) : isErrorHouseholds || isErrorGroups ? (
    <Alert variant="danger">An error occurred</Alert>
  ) : (
    <>
      <Table table={table} />
      {leftOut.length > 0 ? (
        <Form.Text className="text-muted d-block">
          {leftOut.map((household) => (
            <span key={household.id} className="d-block">
              {leftOutSentence(
                household.name,
                household.groupNames,
                effectiveDate,
              )}
            </span>
          ))}
          <span className="d-block">
            A household belongs to at most one household group on a date.
          </span>
        </Form.Text>
      ) : null}
    </>
  );
};

export default HouseholdGroupHouseholdPicker;
